/**
 * The wordless statement that sound is moving.
 *
 * Its whole job is peripheral: a reader scanning a list of twenty rows should
 * be able to see which one is playing without reading a status line. That makes
 * `Idle` a STATE and not an absence — the bars stand at a static rest so the
 * component keeps its footprint, and the row does not reflow by three pixels
 * every time playback starts.
 *
 * The motion itself is not declared here. `.cresultcard__equalizer-bar` owns
 * the 760ms loop, the per-bar delays and — the part that matters — the
 * reduced-motion freeze at full height and 60% opacity. Freezing at full height
 * rather than hiding is deliberate: absence reads as "not playing", and a
 * reader who has asked for reduced motion still has to be able to tell. Nothing
 * here may set `transform` inline, because an inline transform outranks the
 * class and would pin every bar at rest.
 *
 * It is `aria-hidden` without exception. The spoken half of this state is the
 * playback provider's live region; two carriers for one fact is one carrier too
 * many.
 */
"use client";
import type { CSSProperties, ReactElement } from "react";
import { CRC_TONE_COLOR } from "./cresultcard.tokens";
import {
  CresultcardEqualizerState,
  CresultcardTone,
} from "./cresultcard.types";
import "./Cresultcard.css";

const EQUALIZER_BAR_CLASS_NAME = "cresultcard__equalizer-bar";

/** The CSS stagger is authored for five bars; below three it reads as a glitch. */
const MIN_BARS = 3;
const MAX_BARS = 5;
const DEFAULT_BARS = 3;

const EQUALIZER_HEIGHT = 11;
const BAR_WIDTH = 2;
const BAR_GAP = 2;
const BAR_RADIUS = 2;
const FULL_HEIGHT = "100%";

const ROOT_STYLE: CSSProperties = {
  display: "inline-flex",
  alignItems: "flex-end",
  flexShrink: 0,
  gap: BAR_GAP,
  height: EQUALIZER_HEIGHT,
};

export interface CresultcardEqualizerProps {
  readonly state: CresultcardEqualizerState;
  /** 3 to 5. Anything outside that is clamped rather than honoured. */
  readonly bars?: number;
  readonly tone?: CresultcardTone;
}

export function CresultcardEqualizer({
  state,
  bars = DEFAULT_BARS,
  tone = CresultcardTone.Positive,
}: CresultcardEqualizerProps): ReactElement {
  const count = Math.min(Math.max(Math.round(bars), MIN_BARS), MAX_BARS);
  const active = state === CresultcardEqualizerState.Active;
  const barColor = CRC_TONE_COLOR[tone];

  return (
    <span
      aria-hidden
      data-el="Cresultcard/Equalizer/Root"
      data-crc-playing={active}
      style={ROOT_STYLE}
    >
      {Array.from({ length: count }, (_unused, index): number => index).map(
        (index): ReactElement => (
          <span
            className={EQUALIZER_BAR_CLASS_NAME}
            key={index}
            style={{
              width: BAR_WIDTH,
              height: FULL_HEIGHT,
              borderRadius: BAR_RADIUS,
              backgroundColor: barColor,
            }}
          />
        ),
      )}
    </span>
  );
}
